import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';

import { Loader } from '@/components/common/Loader';
import {
  useGetCompetitionLeaderboardQuery,
  useGetCompetitionQuery,
} from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import { formatDateShort } from '@/lib/format';
import type { CompetitionStatus } from '@/types/qclans';

/**
 * Competition detail + leaderboard standings (`GET /v1/competitions/:id` +
 * `/:id/leaderboard`). Standings are clan- or player-keyed depending on the
 * competition's `subjectType`.
 */
export function CocCompetitionDetail() {
  const { t } = useTranslation('coc');
  const { id = '' } = useParams();

  const compQuery = useGetCompetitionQuery(id, { skip: !id });
  const lbQuery = useGetCompetitionLeaderboardQuery({ id, limit: 50 }, { skip: !id });

  const comp = compQuery.data;
  const entries = lbQuery.data?.entries ?? [];

  const statusKnown = (s: CompetitionStatus): s is 'upcoming' | 'active' | 'ended' =>
    s === 'upcoming' || s === 'active' || s === 'ended';

  if (compQuery.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (compQuery.error || !comp) {
    return (
      <div className="px-4 py-8 text-center text-sm text-danger" role="alert">
        {compQuery.error
          ? t(qclansErrorKey(classifyQclansError(compQuery.error)))
          : t('competitions.loadError')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Trophy aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-lg font-bold text-fg">{comp.name}</h1>
        </div>
        {comp.description && <p className="text-sm text-fg-muted">{comp.description}</p>}
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded bg-bg-tertiary px-2 py-0.5 font-semibold text-fg-muted">
            {t(`competitions.metric.${comp.metric}`)}
          </span>
          <span className="rounded bg-bg-tertiary px-2 py-0.5 font-semibold text-fg-muted">
            {t(`competitions.subject.${comp.subjectType}`)}
          </span>
          <span className="rounded bg-bg-tertiary px-2 py-0.5 font-semibold text-fg-muted">
            {statusKnown(comp.status) ? t(`competitions.status.${comp.status}`) : comp.status}
          </span>
        </div>
        <p className="text-[11px] text-fg-muted">
          {t('competitions.dateRange', {
            start: formatDateShort(comp.startsAt),
            end: formatDateShort(comp.endsAt),
          })}
        </p>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="px-1 text-xs font-bold uppercase tracking-wider text-fg-muted">
          {t('competitions.leaderboard.title')}
        </h2>
        {lbQuery.isLoading ? (
          <Loader />
        ) : lbQuery.error ? (
          <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
            {t(qclansErrorKey(classifyQclansError(lbQuery.error)))}
          </p>
        ) : entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-muted">
            {t('competitions.leaderboard.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl bg-bg-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-fg-muted">
                  <th className="px-2 py-2 text-left font-semibold">
                    {t('competitions.leaderboard.rank')}
                  </th>
                  <th className="px-2 py-2 text-left font-semibold">
                    {t('competitions.leaderboard.subject')}
                  </th>
                  <th className="px-2 py-2 text-right font-semibold">
                    {t('competitions.leaderboard.score')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={`${row.subjectTag}-${row.rank}`} className="border-b border-border last:border-0">
                    <td className="px-2 py-2 font-mono text-fg-muted">{row.rank}</td>
                    <td className="px-2 py-2">
                      <span className="font-semibold text-fg">{row.subjectName}</span>
                      <span className="block text-[10px] tabular-nums text-fg-muted">
                        {row.subjectTag}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-semibold text-fg">
                      {row.score.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
